import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "../components/admin/AdminShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Loader2, Copy, Plus, Power, Trash2, Pencil, Star, QrCode, Search, X, Code2,
} from "lucide-react";
import { PALETTES, LAYOUTS } from "../data/stickerTemplates";

const API = "/api";

const CATEGORY_LABELS = {
  vehiculos: "Vehículos",
  ninos: "Niños",
  mascotas: "Mascotas",
  objetos: "Objetos",
  emergencia: "Emergencia",
  minimal: "Minimalistas",
  premium: "Premium",
};

const catLabel = (c) => CATEGORY_LABELS[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : "—");

const paletteOf = (name) => PALETTES[name] || PALETTES.emerald_forest;

/** Vista previa real del sticker: fondo, paleta, icono, textos y el hueco del QR. */
const StickerPreview = ({ tpl, size = "md" }) => {
  const p = paletteOf(tpl.palette);
  const compact = size === "sm";
  return (
    <div
      className="relative flex w-full flex-col items-center justify-between overflow-hidden rounded-xl"
      style={{ background: p.bg, aspectRatio: "3 / 4", padding: compact ? 12 : 18 }}
      data-testid={`sticker-preview-${tpl.id || "nuevo"}`}
    >
      <div className="flex w-full flex-col items-center text-center">
        <span style={{ fontSize: compact ? 26 : 40, lineHeight: 1 }} aria-hidden="true">
          {tpl.emoji || "🚗"}
        </span>
        <p
          className="mt-1 font-extrabold leading-tight"
          style={{ color: p.accent, fontSize: compact ? 12 : 17, textShadow: "0 1px 2px rgba(0,0,0,.25)" }}
        >
          {tpl.title || "Título del sticker"}
        </p>
        {tpl.slogan && (
          <p className="mt-0.5 font-semibold leading-tight" style={{ color: p.accent, opacity: 0.92, fontSize: compact ? 9 : 12 }}>
            {tpl.slogan}
          </p>
        )}
      </div>
      <div
        className="flex items-center justify-center rounded-lg bg-white shadow-sm"
        style={{ width: compact ? 46 : 74, height: compact ? 46 : 74 }}
      >
        <QrCode style={{ width: compact ? 30 : 52, height: compact ? 30 : 52, color: "#0B2545" }} aria-hidden="true" />
      </div>
      <p
        className="w-full text-center leading-tight"
        style={{ color: p.accent, opacity: 0.9, fontSize: compact ? 8 : 10 }}
      >
        {tpl.subline || "AvisoCar · contacto privado y seguro"}
      </p>
      {tpl.featured && (
        <span className="absolute right-2 top-2 rounded-full bg-black/35 p-1" title="Destacada">
          <Star className="h-3 w-3 text-amber-300" aria-hidden="true" />
        </span>
      )}
      {!tpl.active && (
        <span className="absolute inset-x-0 bottom-0 bg-black/65 py-1 text-center text-[10px] font-bold uppercase tracking-wide text-white">
          Borrador · no publicada
        </span>
      )}
    </div>
  );
};

const PaletteSwatch = ({ name, selected, onSelect }) => {
  const p = paletteOf(name);
  return (
    <button
      type="button"
      onClick={() => onSelect(name)}
      title={name}
      aria-label={`Paleta ${name}`}
      aria-pressed={selected}
      className={`h-10 w-10 rounded-lg ring-2 transition-transform hover:scale-105 ${
        selected ? "ring-[#5BC0FF]" : "ring-white/10"
      }`}
      style={{ background: p.bg }}
      data-testid={`sticker-palette-${name}`}
    />
  );
};

const ICON_CHOICES = ["🚗", "🚙", "🛻", "🏍️", "👶", "🧒", "🐕", "🐈", "🎒", "💼", "🔑", "📱",
  "🚨", "⚠️", "❤️", "🛡️", "📞", "✅"];

const EMPTY = {
  __new: true, id: "", category: "vehiculos", title: "", slogan: "", subline: "",
  emoji: "🚗", palette: "emerald_forest", layout: "hero", featured: false,
  active: false, sort_order: 999,
};

export default function AdminStickerTemplates() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [working, setWorking] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [parity, setParity] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sticker-templates`, {
        params: { category: category || undefined, status: status || undefined },
      });
      setData(r.data);
    } catch (_e) {
      toast.error("Error al cargar el catálogo de plantillas");
    } finally {
      setLoading(false);
    }
  }, [category, status]);

  useEffect(() => { load(); }, [load]);

  const templates = useMemo(() => {
    const all = data?.templates || [];
    if (!search.trim()) return all;
    const t = search.trim().toLowerCase();
    return all.filter((x) =>
      [x.id, x.title, x.slogan, x.subline, x.category].some((f) => (f || "").toLowerCase().includes(t)));
  }, [data, search]);

  const paletteNames = useMemo(
    () => (data?.palettes?.length ? data.palettes : Object.keys(PALETTES)), [data]);
  const layoutNames = useMemo(
    () => (data?.layouts?.length ? data.layouts : Object.keys(LAYOUTS)), [data]);

  const save = async () => {
    if (editing.__new && !editing.id.trim()) {
      toast.error("La plantilla necesita un identificador");
      return;
    }
    setWorking(true);
    try {
      const payload = {
        category: editing.category, title: editing.title, slogan: editing.slogan,
        subline: editing.subline, emoji: editing.emoji, palette: editing.palette,
        layout: editing.layout, featured: !!editing.featured,
        active: !!editing.active, sort_order: Number(editing.sort_order) || 0,
      };
      if (editing.__new) await axios.post(`${API}/admin/sticker-templates`, { ...payload, id: editing.id.trim() });
      else await axios.put(`${API}/admin/sticker-templates/${editing.id}`, payload);
      toast.success(editing.active ? "Plantilla guardada y publicada" : "Plantilla guardada como borrador");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(`No se pudo guardar: ${err?.response?.data?.detail || "error"}`);
    } finally { setWorking(false); }
  };

  const toggleActive = async (tpl) => {
    try {
      await axios.put(`${API}/admin/sticker-templates/${tpl.id}`, { active: !tpl.active });
      toast.success(tpl.active ? "Plantilla despublicada" : "Plantilla publicada");
      load();
    } catch (_e) { toast.error("No se pudo cambiar el estado"); }
  };

  const duplicate = async (tpl) => {
    const newId = window.prompt("Identificador de la copia:", `${tpl.id}-copia`);
    if (!newId) return;
    try {
      await axios.post(`${API}/admin/sticker-templates/${tpl.id}/duplicate`, { new_id: newId });
      toast.success("Copia creada como borrador (inactiva)");
      load();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || "duplicado"}`); }
  };

  const remove = async (tpl) => {
    if (!window.confirm(`¿Eliminar la plantilla "${tpl.title || tpl.id}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/sticker-templates/${tpl.id}`);
      toast.success("Plantilla eliminada");
      load();
    } catch (err) {
      toast.error(err?.response?.status === 409
        ? "No se puede eliminar: hay clientes con diseños guardados que la usan"
        : "Error al eliminar");
    }
  };

  const checkParity = async () => {
    try {
      const r = await axios.get(`${API}/admin/sticker-templates/parity`);
      setParity(r.data);
    } catch (_e) { toast.error("Error al comprobar la paridad del catálogo"); }
  };

  const selectCls = "h-10 rounded-lg border border-white/15 bg-[#0B2545] px-3 text-sm text-slate-200 focus:border-[#1B84FF] focus:outline-none";
  const fieldCls = "border-white/15 bg-[#14375F] text-white";

  return (
    <AdminShell
      testid="admin-sticker-studio"
      title="Estudio de plantillas de stickers"
      description="Diseña visualmente las plantillas que ven los clientes en la web y en la app. El catálogo es único: lo que publiques aquí es lo que se ofrece en todas las plataformas."
      actions={
        <>
          <Button
            onClick={() => { setEditing({ ...EMPTY, category: data?.categories?.[0] || "vehiculos" }); setShowAdvanced(false); }}
            className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]"
            data-testid="admin-stickers-new-btn"
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" /> Nueva plantilla
          </Button>
          <Button variant="outline" onClick={checkParity} className="border-white/15 text-slate-300"
                  data-testid="admin-stickers-parity-btn">
            Comprobar catálogo
          </Button>
        </>
      }
    >
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden="true" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, texto o identificador"
            className="h-10 border-white/15 bg-[#0B2545] pl-9 text-white"
            data-testid="admin-stickers-search"
          />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={selectCls}
                aria-label="Filtrar por categoría" data-testid="admin-stickers-category-filter">
          <option value="">Todas las categorías</option>
          {(data?.categories || []).map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}
                aria-label="Filtrar por estado" data-testid="admin-stickers-status-filter">
          <option value="">Publicadas y borradores</option>
          <option value="active">Solo publicadas</option>
          <option value="inactive">Solo borradores</option>
        </select>
        <span className="text-sm text-slate-400" data-testid="admin-stickers-count">
          {loading ? "Cargando…" : `${templates.length} plantilla${templates.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {parity && (
        <div
          role="status"
          className={`mb-6 rounded-xl border p-4 text-sm ${parity.parity
            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
            : "border-rose-500/30 bg-rose-500/10 text-rose-200"}`}
          data-testid="admin-stickers-parity-result"
        >
          {parity.parity
            ? `Catálogo íntegro: ${parity.canonical_total} plantillas publicadas coinciden con el catálogo de referencia.`
            : `Atención: faltan ${parity.missing_in_canonical.length}, sobran ${parity.extra_in_canonical.length} y hay ${parity.category_drift.length} con categoría distinta.`}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5BC0FF]" /></div>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0B2545] p-12 text-center" data-testid="admin-stickers-empty">
          <p className="font-medium text-slate-300">No hay plantillas con estos filtros</p>
          <p className="mt-1 text-sm text-slate-500">Cambia la categoría o el estado, o crea una plantilla nueva.</p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="admin-stickers-gallery">
          {templates.map((t) => (
            <article
              key={t.id}
              className="group rounded-2xl border border-white/10 bg-[#0B2545] p-3 transition-colors hover:border-[#1B84FF]/40"
              data-testid={`admin-sticker-card-${t.id}`}
            >
              <StickerPreview tpl={t} size="sm" />
              <div className="mt-3 px-1">
                <p className="truncate font-semibold text-white">{t.title || t.id}</p>
                <p className="mt-0.5 text-xs text-slate-400">
                  {catLabel(t.category)} · {t.active ? "Publicada" : "Borrador"}
                </p>
                <p className="mt-0.5 text-[11px] text-slate-500">
                  {t.usage_count > 0 ? `${t.usage_count} diseño(s) de clientes` : "Sin uso todavía"}
                </p>
              </div>
              <div className="mt-3 flex items-center gap-1 border-t border-white/10 pt-2">
                <button
                  onClick={() => { setEditing({ ...t }); setShowAdvanced(false); }}
                  className="flex-1 rounded-lg px-2 py-1.5 text-xs font-medium text-slate-200 hover:bg-white/10"
                  data-testid={`admin-sticker-edit-${t.id}`}
                >
                  <Pencil className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" /> Editar
                </button>
                <button
                  onClick={() => toggleActive(t)}
                  title={t.active ? "Despublicar" : "Publicar"}
                  aria-label={t.active ? `Despublicar ${t.id}` : `Publicar ${t.id}`}
                  className={`rounded-lg p-1.5 hover:bg-white/10 ${t.active ? "text-emerald-300" : "text-slate-400"}`}
                  data-testid={`admin-sticker-toggle-${t.id}`}
                >
                  <Power className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  onClick={() => duplicate(t)}
                  title="Duplicar"
                  aria-label={`Duplicar ${t.id}`}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-white/10"
                  data-testid={`admin-sticker-duplicate-${t.id}`}
                >
                  <Copy className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  onClick={() => remove(t)}
                  title="Eliminar"
                  aria-label={`Eliminar ${t.id}`}
                  className="rounded-lg p-1.5 text-rose-400 hover:bg-rose-500/15"
                  data-testid={`admin-sticker-delete-${t.id}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/75 p-0 sm:items-center sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Editor visual de plantilla"
          data-testid="admin-sticker-modal"
        >
          <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl border border-white/15 bg-[#0B2545] sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <h2 className="text-base font-semibold text-white md:text-lg">
                {editing.__new ? "Nueva plantilla de sticker" : `Editar “${editing.title || editing.id}”`}
              </h2>
              <button
                onClick={() => setEditing(null)}
                className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white"
                aria-label="Cerrar editor"
                data-testid="admin-sticker-close-btn"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="grid flex-1 gap-6 overflow-y-auto p-5 md:grid-cols-[260px_1fr]">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Vista previa</p>
                <StickerPreview tpl={editing} />
                <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
                  El QR real del cliente se inserta en el hueco blanco al generar su sticker.
                </p>
              </div>

              <div className="space-y-5">
                {editing.__new && (
                  <div>
                    <Label htmlFor="tpl-id" className="text-sm text-slate-300">Identificador interno</Label>
                    <Input
                      id="tpl-id"
                      value={editing.id}
                      onChange={(e) => setEditing({ ...editing, id: e.target.value })}
                      placeholder="p. ej. vehiculo-clasico-azul"
                      className={fieldCls}
                      data-testid="admin-sticker-field-id"
                    />
                  </div>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="tpl-category" className="text-sm text-slate-300">Categoría</Label>
                    <select
                      id="tpl-category"
                      value={editing.category}
                      onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                      className={`w-full ${selectCls}`}
                      data-testid="admin-sticker-field-category"
                    >
                      {(data?.categories || ["vehiculos"]).map((c) => (
                        <option key={c} value={c}>{catLabel(c)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="tpl-layout" className="text-sm text-slate-300">Composición</Label>
                    <select
                      id="tpl-layout"
                      value={editing.layout}
                      onChange={(e) => setEditing({ ...editing, layout: e.target.value })}
                      className={`w-full ${selectCls}`}
                      data-testid="admin-sticker-field-layout"
                    >
                      {layoutNames.map((l) => (
                        <option key={l} value={l}>{LAYOUTS[l] ? `${l} — ${LAYOUTS[l]}` : l}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="tpl-title" className="text-sm text-slate-300">Título principal</Label>
                  <Input id="tpl-title" value={editing.title || ""}
                         onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                         className={fieldCls} data-testid="admin-sticker-field-title" />
                </div>
                <div>
                  <Label htmlFor="tpl-slogan" className="text-sm text-slate-300">Frase destacada</Label>
                  <Input id="tpl-slogan" value={editing.slogan || ""}
                         onChange={(e) => setEditing({ ...editing, slogan: e.target.value })}
                         className={fieldCls} data-testid="admin-sticker-field-slogan" />
                </div>
                <div>
                  <Label htmlFor="tpl-subline" className="text-sm text-slate-300">Texto de apoyo</Label>
                  <Input id="tpl-subline" value={editing.subline || ""}
                         onChange={(e) => setEditing({ ...editing, subline: e.target.value })}
                         className={fieldCls} data-testid="admin-sticker-field-subline" />
                </div>

                <div>
                  <p className="mb-2 text-sm text-slate-300">Icono</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="admin-sticker-icon-picker">
                    {ICON_CHOICES.map((ic) => (
                      <button
                        key={ic}
                        type="button"
                        onClick={() => setEditing({ ...editing, emoji: ic })}
                        aria-label={`Icono ${ic}`}
                        aria-pressed={editing.emoji === ic}
                        className={`h-9 w-9 rounded-lg text-lg ring-2 transition-transform hover:scale-105 ${
                          editing.emoji === ic ? "bg-white/10 ring-[#5BC0FF]" : "ring-white/10"
                        }`}
                        data-testid={`admin-sticker-icon-${ic}`}
                      >
                        {ic}
                      </button>
                    ))}
                    <Input
                      value={editing.emoji || ""}
                      onChange={(e) => setEditing({ ...editing, emoji: e.target.value })}
                      className={`h-9 w-20 text-center ${fieldCls}`}
                      aria-label="Icono personalizado"
                      data-testid="admin-sticker-field-emoji"
                    />
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-sm text-slate-300">
                    Paleta de colores <span className="text-slate-500">({editing.palette})</span>
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="admin-sticker-palette-picker">
                    {paletteNames.map((name) => (
                      <PaletteSwatch
                        key={name}
                        name={name}
                        selected={editing.palette === name}
                        onSelect={(n) => setEditing({ ...editing, palette: n })}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-5 rounded-xl border border-white/10 bg-[#14375F]/60 p-4">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-200">
                    <input type="checkbox" checked={!!editing.active} className="accent-[#1B84FF]"
                           onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                           data-testid="admin-sticker-field-active" />
                    Publicada (visible para los clientes)
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-200">
                    <input type="checkbox" checked={!!editing.featured} className="accent-amber-400"
                           onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                           data-testid="admin-sticker-field-featured" />
                    Destacada
                  </label>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => setShowAdvanced((v) => !v)}
                    className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-slate-200"
                    data-testid="admin-sticker-advanced-toggle"
                  >
                    <Code2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {showAdvanced ? "Ocultar detalles técnicos" : "Mostrar detalles técnicos"}
                  </button>
                  {showAdvanced && (
                    <div className="mt-3 space-y-3 rounded-xl border border-white/10 bg-[#050F1D] p-4" data-testid="admin-sticker-advanced">
                      <div>
                        <Label htmlFor="tpl-order" className="text-xs text-slate-400">Orden de aparición</Label>
                        <Input id="tpl-order" type="number" value={editing.sort_order}
                               onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })}
                               className={fieldCls} data-testid="admin-sticker-field-order" />
                      </div>
                      <dl className="grid grid-cols-2 gap-2 font-mono text-[11px] text-slate-400">
                        <dt>id</dt><dd className="text-slate-200">{editing.id || "—"}</dd>
                        <dt>palette</dt><dd className="text-slate-200">{editing.palette}</dd>
                        <dt>layout</dt><dd className="text-slate-200">{editing.layout}</dd>
                        <dt>version</dt><dd className="text-slate-200">{editing.version ?? "—"}</dd>
                      </dl>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 border-t border-white/10 bg-[#081C33] px-5 py-4">
              <Button onClick={save} disabled={working} className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]"
                      data-testid="admin-sticker-save-btn">
                {working ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : "Guardar plantilla"}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(null)} className="text-slate-300"
                      data-testid="admin-sticker-cancel-btn">
                Cancelar
              </Button>
              <p className="ml-auto text-xs text-slate-500">
                {editing.active ? "Se publicará al guardar." : "Se guardará como borrador, invisible para los clientes."}
              </p>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
